import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MdInfoOutline } from 'react-icons/md';

const SUITE_GUIDE_STEPS = [
  {
    color: '#f59e0b',
    title: 'Prerequisites',
    desc: 'Make sure you have at least one Test Step configured (via the Test Steps page) before building a suite. Steps can also include Groups or Custom Functions.',
  },
  {
    color: '#fbbf24',
    title: 'Create a Suite',
    desc: 'Click "+ Create New Test Suite", give it a name and optional description, then add steps in order using the searchable selector.',
  },
  {
    color: '#d97706',
    title: 'Order & Chain Steps',
    desc: 'Drag rows to reorder. For chaining (e.g. Login → Create), add Capture Rules on earlier steps ($.data.id → myVar) and use {{myVar}} in later steps.',
  },
  {
    color: '#64748b',
    title: 'Static vs Data-Driven',
    desc: '"Static" runs the step once using its fixed payload. "Data-Driven" runs it once per CSV row — one test per row in your dataset.',
  },
  {
    color: '#475569',
    title: 'Execute & Review',
    desc: 'Click Execute on any suite. Results are auto-saved and viewable in Execution Results with per-row pass/fail status and full response bodies.',
  },
];

function SuiteQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>Test Suite Builder — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {SUITE_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const createEmptySuiteStep = () => ({
  testStepId: '', // Select value which could be stepId, group:groupId, or fn:fnId
  payloadTypeOverride: 'specific',
  storeAs: ''
});

const GROUP_OPTION_PREFIX = 'group:';
const FUNCTION_OPTION_PREFIX = 'fn:';

const normalizePayloadMode = (value) => {
  if (value === 'testData') return 'testData';
  return 'specific';
};

function TestSuiteBuilder({ onStats, onExecutionComplete }) {
  const [suites, setSuites] = useState([]);
  const [testSteps, setTestSteps] = useState([]);
  const [testGroups, setTestGroups] = useState([]);
  const [customFunctions, setCustomFunctions] = useState([]);
  const [apis, setApis] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [executingId, setExecutingId] = useState(null);
  const [suiteToDelete, setSuiteToDelete] = useState(null);
  const [deletingSuite, setDeletingSuite] = useState(false);
  const [suiteToView, setSuiteToView] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    orderedItems: [createEmptySuiteStep()]
  });
  const [activeSelectorIdx, setActiveSelectorIdx] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [sidePanelIdx, setSidePanelIdx] = useState(null);

  const compactSelectStyle = {
    width: '100%',
    height: '42px',
    padding: '0 12px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-main)'
  };


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [suitesRes, testStepsRes, testGroupsRes, customFunctionsRes, apisRes] = await Promise.all([
        axios.get('/api/suites'),
        axios.get('/api/teststeps'),
        axios.get('/api/testgroups'),
        axios.get('/api/custom-functions'),
        axios.get('/api/apis')
      ]);
      setSuites(Array.isArray(suitesRes.data) ? suitesRes.data : []);
      setTestSteps(Array.isArray(testStepsRes.data) ? testStepsRes.data : []);
      setTestGroups(Array.isArray(testGroupsRes.data) ? testGroupsRes.data : []);
      setCustomFunctions(Array.isArray(customFunctionsRes.data) ? customFunctionsRes.data : []);
      setApis(Array.isArray(apisRes.data) ? apisRes.data : []);
      if (onStats) onStats();
    } catch (fetchError) {
      setError('Failed to fetch data: ' + fetchError.message);
    }
  };

  const getTestStepInfo = (stepId) => testSteps.find((s) => s.id === stepId) || null;
  const getGroupInfo = (groupId) => testGroups.find((g) => g.id === groupId) || null;

  const buildGroupOptionValue = (groupId) => `${GROUP_OPTION_PREFIX}${groupId}`;
  const buildFunctionOptionValue = (fnId) => `${FUNCTION_OPTION_PREFIX}${fnId}`;

  const parseSuiteSelection = (selectedValue) => {
    const value = String(selectedValue || '').trim();
    if (!value) return { type: 'none', id: '' };
    if (value.startsWith(GROUP_OPTION_PREFIX)) {
      return { type: 'group', id: value.slice(GROUP_OPTION_PREFIX.length) };
    }
    if (value.startsWith(FUNCTION_OPTION_PREFIX)) {
      return { type: 'function', id: value.slice(FUNCTION_OPTION_PREFIX.length) };
    }
    return { type: 'step', id: value };
  };

  const getModeAvailability = (step) => {
    if (!step) return { canUseSpecific: false, canUseTestData: false };
    return {
      canUseSpecific: Boolean(step.specificPayload && step.specificPayload.trim()),
      canUseTestData: Boolean(step.testDataId && step.testDataId.trim())
    };
  };

  const resolveModeForStep = (selectedValue) => {
    const selection = parseSuiteSelection(selectedValue);
    if (selection.type !== 'step') return 'specific';
    const step = getTestStepInfo(selection.id);
    const { canUseSpecific, canUseTestData } = getModeAvailability(step);
    
    // Always prefer specific (Static) if available
    if (canUseSpecific) return 'specific';
    if (canUseTestData) return 'testData';
    return 'specific';
  };

  const getSuiteItemsForDisplay = (suite) => {
    if (!suite || !Array.isArray(suite.orderedItems)) return [];
    return suite.orderedItems.map((item, index) => {
      if (item.type === 'group') {
        const group = getGroupInfo(item.id);
        return { key: `g-${index}`, name: group ? group.name : 'Unknown Group', type: 'group' };
      }
      if (item.type === 'function') {
        const fn = customFunctions.find(f => f.id === item.id);
        return { key: `f-${index}`, name: fn ? fn.name : 'Unknown Function', type: 'function' };
      }
      const step = getTestStepInfo(item.id);
      return { 
        key: `s-${index}`, 
        name: step ? step.name : 'Unknown Step', 
        type: 'step', 
        runMode: item.payloadTypeOverride 
      };
    });
  };

  const buildSuitePayload = () => {
    const name = formData.name.trim();
    if (!name) return { error: 'Suite name is required' };
    
    const orderedItems = formData.orderedItems
      .map(item => {
        const selection = parseSuiteSelection(item.testStepId);
        if (selection.type === 'none') return null;
        return {
          type: selection.type,
          id: selection.id,
          payloadTypeOverride: item.payloadTypeOverride,
          storeAs: (selection.type === 'function' ? item.storeAs : '')
        };
      })
      .filter(Boolean);

    if (orderedItems.length === 0) return { error: 'Add at least one item' };
    return { name, description: formData.description.trim(), orderedItems };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const payload = buildSuitePayload();
    if (payload.error) { setError(payload.error); return; }

    try {
      if (editingId) {
        await axios.put(`/api/suites/${editingId}`, payload);
        setSuccess('Suite updated');
      } else {
        await axios.post('/api/suites', payload);
        setSuccess('Suite created');
      }
      setShowForm(false); setEditingId(null);
      setFormData({ name: '', description: '', orderedItems: [createEmptySuiteStep()] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleEdit = (suite) => {
    setEditingId(suite.id);
    setFormData({
      name: suite.name || '',
      description: suite.description || '',
      orderedItems: Array.isArray(suite.orderedItems) 
        ? suite.orderedItems.map(item => ({
            testStepId: item.type === 'group' ? buildGroupOptionValue(item.id) : (item.type === 'function' ? buildFunctionOptionValue(item.id) : item.id),
            payloadTypeOverride: item.payloadTypeOverride || 'specific',
            storeAs: item.storeAs || ''
          }))
        : [createEmptySuiteStep()]
    });
    setShowForm(true);
  };

  const handleExecute = async (id) => {
    setExecutingId(id); setError('');
    try {
      const res = await axios.post(`/api/execution/suite/${id}`);
      if (onExecutionComplete) onExecutionComplete(res.data);
      setSuccess(`Executed! ${res.data.summary.passed}/${res.data.summary.total} passed`);
      setTimeout(() => { window.location.href = '/#results'; }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setExecutingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeletingSuite(true);
      await axios.delete(`/api/suites/${suiteToDelete.id}`);
      fetchData(); setSuiteToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingSuite(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
    // Visual feedback placeholder for some browsers
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIndex) return;

    const newItems = [...formData.orderedItems];
    const draggedItem = newItems[draggedIdx];
    
    // Remove from old position and insert at new position
    newItems.splice(draggedIdx, 1);
    newItems.splice(targetIndex, 0, draggedItem);
    
    setFormData({ ...formData, orderedItems: newItems });
    setDraggedIdx(null);
  };

  const getFilteredItems = () => {
    const term = searchTerm.toLowerCase();
    const items = [];
    
    if (activeTypeFilter === 'all' || activeTypeFilter === 'step') {
      testSteps.forEach(s => items.push({ id: s.id, name: s.name, type: 'step', search: `[step] ${s.name}`.toLowerCase() }));
    }
    if (activeTypeFilter === 'all' || activeTypeFilter === 'group') {
      testGroups.forEach(g => items.push({ id: buildGroupOptionValue(g.id), name: g.name, type: 'group', search: `[group] ${g.name}`.toLowerCase() }));
    }
    if (activeTypeFilter === 'all' || activeTypeFilter === 'function') {
      customFunctions.forEach(f => items.push({ id: buildFunctionOptionValue(f.id), name: f.name, type: 'function', search: `[fn] ${f.name}`.toLowerCase() }));
    }

    return items.filter(it => it.search.includes(term));
  };

  const SearchableSelector = ({ idx, currentId, onSelect }) => {
    const isOpen = activeSelectorIdx === idx;
    const selectedItem = currentId ? getFilteredItems().find(it => it.id === currentId) || { name: 'Unknown Item', type: 'unknown' } : null;
    
    const triggerStyle = {
      ...compactSelectStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      background: 'var(--bg-card)',
      textAlign: 'left'
    };

    const dropdownStyle = {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      background: 'var(--bg-panel)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
      marginTop: '5px',
      padding: '12px',
      maxHeight: '400px',
      display: 'flex',
      flexDirection: 'column'
    };

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={triggerStyle} onClick={() => { setActiveSelectorIdx(isOpen ? null : idx); setSearchTerm(''); }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedItem ? (
              <>
                <span className={`badge badge-${selectedItem.type === 'step' ? 'success' : (selectedItem.type === 'group' ? 'primary' : 'warning')}`} style={{ marginRight: '8px', fontSize: '10px' }}>
                  {selectedItem.type.toUpperCase()}
                </span>
                {selectedItem.name}
              </>
            ) : 'Select Item...'}
          </span>
          <span style={{ fontSize: '12px', opacity: 0.5 }}>{isOpen ? '▲' : '▼'}</span>
        </div>

        {isOpen && (
          <div style={dropdownStyle}>
            <input
              type="text"
              placeholder="Search steps, groups, functions..."
              autoFocus
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ ...compactSelectStyle, marginBottom: '10px' }}
              onClick={e => e.stopPropagation()}
            />
            
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
              {['all', 'step', 'group', 'function'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`btn btn-${activeTypeFilter === type ? 'primary' : 'secondary'}`}
                  style={{ flex: 1, fontSize: '11px', padding: '4px' }}
                  onClick={(e) => { e.stopPropagation(); setActiveTypeFilter(type); }}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                </button>
              ))}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, minHeight: '100px' }}>
              {getFilteredItems().map(it => (
                <div
                  key={it.id}
                  style={{
                    padding: '10px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    borderBottom: '1px solid var(--border)',
                    backgroundColor: currentId === it.id ? 'var(--bg-panel-soft)' : 'transparent'
                  }}
                  onMouseOver={e => e.currentTarget.style.backgroundColor = 'var(--bg-panel-soft)'}
                  onMouseOut={e => e.currentTarget.style.backgroundColor = currentId === it.id ? 'var(--bg-panel-soft)' : 'transparent'}
                  onClick={() => {
                    onSelect(it.id);
                    setActiveSelectorIdx(null);
                  }}
                >
                  <span className={`badge badge-${it.type === 'step' ? 'success' : (it.type === 'group' ? 'primary' : 'warning')}`} style={{ marginRight: '8px', fontSize: '9px' }}>
                    {it.type.toUpperCase()}
                  </span>
                  <span style={{ fontSize: '13px' }}>{it.name}</span>
                </div>
              ))}
              {getFilteredItems().length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>No items found</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const SidePanel = ({ idx }) => {
    const item = formData.orderedItems[idx];
    const selection = parseSuiteSelection(item?.testStepId || '');
    const step = selection.type === 'step' ? getTestStepInfo(selection.id) : null;
    const api = step ? apis.find(a => a.id === step.apiId) : null;

    // Local state for editing the BASE step/api
    const [localUrl, setLocalUrl] = useState('');
    const [localHeaders, setLocalHeaders] = useState('');
    const [localPayload, setLocalPayload] = useState('');
    const [localRules, setLocalRules] = useState([]);
    const [localExpectedStatus, setLocalExpectedStatus] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (api) {
        setLocalUrl(api.url || '');
        setLocalHeaders(typeof api.headers === 'object' ? JSON.stringify(api.headers, null, 2) : (api.headers || ''));
      } else {
        setLocalUrl('');
        setLocalHeaders('');
      }
      if (step) {
        setLocalPayload(step.specificPayload || '');
        setLocalRules(step.responseCaptureRules || []);
        setLocalExpectedStatus(step.expectedStatus || (api ? api.expectedStatus : '') || '200');
      } else {
        setLocalPayload('');
        setLocalRules([]);
        setLocalExpectedStatus('');
      }
    }, [idx, step, api]);

    if (!item || selection.type !== 'step') return null;

    const handleRuleChange = (rIdx, field, val) => {
      const rules = [...localRules];
      rules[rIdx] = { ...rules[rIdx], [field]: val };
      setLocalRules(rules);
    };

    const addRule = () => {
      setLocalRules([...localRules, { storeAs: '', path: '' }]);
    };

    const removeRule = (rIdx) => {
      setLocalRules(localRules.filter((_, i) => i !== rIdx));
    };

    const handleSaveChanges = async () => {
      if (!step || !api) return;
      setIsSaving(true);
      try {
        // 1. Update API
        let parsedHeaders = localHeaders;
        try { parsedHeaders = JSON.parse(localHeaders); } catch(e) { /* ignore if not valid JSON, keep as string */ }
        
        await axios.put(`/api/apis/${api.id}`, { 
          ...api,
          url: localUrl, 
          headers: parsedHeaders 
        });

        // 2. Update Test Step
        await axios.put(`/api/teststeps/${step.id}`, { 
          ...step,
          specificPayload: localPayload,
          responseCaptureRules: localRules,
          expectedStatus: localExpectedStatus
        });

        setSuccess('Base Test Step and API updated successfully!');
        fetchData(); // Refresh global data
        setSidePanelIdx(null);
      } catch (err) {
        setError('Failed to update: ' + (err.response?.data?.error || err.message));
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="side-panel" style={{ 
        position: 'fixed', 
        right: 0, 
        width: '450px', 
        height: '100%', 
        backgroundColor: 'var(--bg-panel-solid)', 
        borderLeft: '1px solid var(--border)', 
        boxShadow: '-10px 0 30px rgba(0,0,0,0.5)', 
        zIndex: 2000, 
        padding: '24px', 
        overflowY: 'auto' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
          <h3 style={{ margin: 0 }}>Edit Base Test Step</h3>
          <button className="modal-close" onClick={() => setSidePanelIdx(null)} style={{ position: 'static' }}>×</button>
        </div>

        <div className="form-group">
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>API URL</label>
          <input 
            type="text"
            value={localUrl}
            onChange={e => setLocalUrl(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-panel-soft)' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>EXPECTED STATUS</label>
          <input 
            type="number"
            value={localExpectedStatus}
            onChange={e => setLocalExpectedStatus(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-panel-soft)' }}
            placeholder="e.g., 200"
          />
          <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>The HTTP status code you expect for this step.</small>
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>HEADERS (JSON)</label>
          <textarea 
            value={localHeaders}
            onChange={e => setLocalHeaders(e.target.value)}
            rows={4}
            style={{ fontFamily: 'monospace', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-panel-soft)' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>SPECIFIC PAYLOAD (JSON)</label>
          <textarea 
            value={localPayload}
            onChange={e => setLocalPayload(e.target.value)}
            rows={6}
            style={{ fontFamily: 'monospace', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-panel-soft)' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>RESPONSE CAPTURE RULES</label>
          <div style={{ display: 'grid', gap: '8px' }}>
            {localRules.map((rule, rIdx) => (
              <div key={rIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px' }}>
                <input type="text" placeholder="Var" value={rule.storeAs} onChange={e => handleRuleChange(rIdx, 'storeAs', e.target.value)} style={{ fontSize: '11px', height: '30px' }} />
                <input type="text" placeholder="Path" value={rule.path} onChange={e => handleRuleChange(rIdx, 'path', e.target.value)} style={{ fontSize: '11px', height: '30px' }} />
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeRule(rIdx)}>-</button>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: '10px' }} onClick={addRule}>+ Add Rule</button>
        </div>

        <div style={{ marginTop: 'auto', paddingTop: '20px', display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setSidePanelIdx(null)}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleSaveChanges} disabled={isSaving}>
            {isSaving ? 'Updating...' : 'Update Base Step'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h1>Test Suite Builder</h1>
      <SuiteQuickStartGuide />
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <button className="btn btn-primary" onClick={() => {
        setShowForm(!showForm); setEditingId(null);
        setFormData({ name: '', description: '', orderedItems: [createEmptySuiteStep()] });
      }}>
        {showForm ? 'Cancel' : '+ Create New Test Suite'}
      </button>

      {showForm && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>{editingId ? 'Edit Suite' : 'New Suite'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label>Name *</label>
              <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div className="form-group"><label>Description</label>
              <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
            </div>

            <h3 style={{ marginTop: '20px' }}>Steps</h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              {formData.orderedItems.map((item, idx) => {
                const selection = parseSuiteSelection(item.testStepId);
                const step = selection.type === 'step' ? getTestStepInfo(selection.id) : null;
                const avail = getModeAvailability(step);
                return (
                  <div 
                    key={idx} 
                    draggable 
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, idx)}
                    style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: '12px', 
                      alignItems: 'center', 
                      padding: '12px', 
                      border: '1px solid var(--border)', 
                      borderRadius: '12px',
                      backgroundColor: draggedIdx === idx ? 'var(--bg-panel-soft)' : 'var(--bg-panel)',
                      cursor: 'default',
                      opacity: draggedIdx === idx ? 0.5 : 1
                    }}
                  >
                    <div 
                      style={{ 
                        cursor: 'grab', 
                        fontSize: '20px', 
                        color: 'var(--text-muted)', 
                        minWidth: '24px',
                        textAlign: 'center',
                        userSelect: 'none'
                      }}
                      title="Drag to reorder"
                    >
                      ::
                    </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px' }}>
                    <strong>Step {idx + 1}</strong>
                  </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <SearchableSelector 
                        idx={idx} 
                        currentId={item.testStepId} 
                        onSelect={(newId) => {
                          const newItems = [...formData.orderedItems];
                          newItems[idx] = { 
                            ...newItems[idx], 
                            testStepId: newId, 
                            payloadTypeOverride: resolveModeForStep(newId) 
                          };
                          setFormData({...formData, orderedItems: newItems});
                        }} 
                      />
                    </div>

                    {selection.type === 'step' ? (
                      <div className="btn-toggle-group" style={{ minWidth: '180px' }}>
                        <button type="button" className={`btn-toggle ${item.payloadTypeOverride === 'specific' ? 'active' : ''}`} disabled={!avail.canUseSpecific} onClick={() => {
                          const newItems = [...formData.orderedItems]; newItems[idx].payloadTypeOverride = 'specific'; setFormData({...formData, orderedItems: newItems});
                        }}>Static</button>
                        <button type="button" className={`btn-toggle ${item.payloadTypeOverride === 'testData' ? 'active' : ''}`} disabled={!avail.canUseTestData} onClick={() => {
                          const newItems = [...formData.orderedItems]; newItems[idx].payloadTypeOverride = 'testData'; setFormData({...formData, orderedItems: newItems});
                        }}>Data-Driven</button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', minWidth: '180px' }}>
                        {selection.type === 'function' ? (
                          <input 
                            type="text" 
                            placeholder="Store Result As"
                            value={item.storeAs || ''}
                            onChange={(e) => {
                              const newItems = [...formData.orderedItems];
                              newItems[idx].storeAs = e.target.value;
                              setFormData({ ...formData, orderedItems: newItems });
                            }}
                            style={{ ...compactSelectStyle, height: '32px', fontSize: '11px' }}
                          />
                        ) : (
                          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                            {selection.type === 'group' ? 'Group Steps' : '-'}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', minWidth: '160px', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-secondary btn-icon-sq" onClick={() => {
                        const newItems = [...formData.orderedItems]; newItems.splice(idx + 1, 0, createEmptySuiteStep()); setFormData({...formData, orderedItems: newItems});
                      }}>+</button>
                      <button type="button" className="btn btn-danger btn-icon-sq" onClick={() => {
                        const newItems = formData.orderedItems.filter((_, i) => i !== idx);
                        setFormData({...formData, orderedItems: newItems.length > 0 ? newItems : [createEmptySuiteStep()]});
                        if (sidePanelIdx === idx) setSidePanelIdx(null);
                      }}>-</button>
                      {selection.type === 'step' && (
                        <button 
                          type="button" 
                          className={`btn ${sidePanelIdx === idx ? 'btn-success' : 'btn-primary'} btn-icon-sq`} 
                          onClick={() => setSidePanelIdx(sidePanelIdx === idx ? null : idx)}
                          title="Edit API Details"
                        >
                          ✎
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="submit" className="btn btn-success" style={{ marginTop: '20px' }}>{editingId ? 'Update Suite' : 'Create Suite'}</button>
          </form>
        </div>
      )}

      <div className="results-layout" style={{ marginTop: '20px' }}>
        <div className="results-column">
          <table className="table" style={{ marginTop: '30px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Suite</th>
                <th style={{ textAlign: 'center', width: '180px' }}>Item Count</th>
                <th style={{ textAlign: 'right', width: '300px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suites.map(s => (
                <tr key={s.id} style={{ verticalAlign: 'middle' }}>
                  <td style={{ textAlign: 'left' }}>
                    <div style={{ padding: '8px 0' }}>
                      <strong>{s.name}</strong><br/>
                      <small style={{ color: 'var(--text-muted)' }}>{s.description}</small>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span style={{ fontSize: '14px' }}>{(s.orderedItems || []).length} items</span>
                      <button className="btn-view" onClick={() => setSuiteToView(s)}>View</button>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-success" onClick={() => handleExecute(s.id)} disabled={executingId === s.id}>{executingId === s.id ? '...' : 'Execute'}</button>
                      <button className="btn btn-primary" onClick={() => handleEdit(s)}>Edit</button>
                      <button className="btn btn-danger" onClick={() => setSuiteToDelete(s)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sidePanelIdx !== null && (
          <div className="results-column">
             <SidePanel idx={sidePanelIdx} />
          </div>
        )}
      </div>

      {suiteToView && (
        <div className="modal-overlay" onClick={() => setSuiteToView(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>{suiteToView.name}</h2>
              <button className="modal-close" onClick={() => setSuiteToView(null)}>×</button>
            </div>
            
            <div className="modal-body" style={{ maxHeight: '450px', overflowY: 'auto', padding: '20px' }}>
              <p style={{ marginBottom: '15px', color: 'var(--text-muted)', fontSize: '14px' }}>
                {suiteToView.description || 'No description provided.'}
              </p>
              
              <div style={{ display: 'grid', gap: '10px' }}>
                {getSuiteItemsForDisplay(suiteToView).map((it, i) => (
                  <div key={it.key} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: 'var(--bg-panel-soft)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold', width: '24px' }}>
                        {i + 1}.
                      </span>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>{it.name}</div>
                        <div style={{ marginTop: '4px' }}>
                          <span className={`badge badge-${it.type === 'step' ? 'success' : (it.type === 'group' ? 'primary' : 'warning')}`} style={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            {it.type}
                          </span>
                          {it.runMode && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                              • {it.runMode === 'testData' ? 'Data-Driven' : 'Static'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSuiteToView(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {suiteToDelete && (
        <div className="modal-overlay" onClick={() => setSuiteToDelete(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Delete "{suiteToDelete.name}"?</h3>
            <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingSuite}>Delete</button>
            <button className="btn btn-secondary" onClick={() => setSuiteToDelete(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestSuiteBuilder;
